import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePermissions } from '@/contexts/PermissionsContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

/**
 * Route protégée par permission
 * Redirige vers la page d'accès refusé si l'utilisateur n'a pas la permission
 * 
 * Usage dans App.js:
 * <Route 
 *   path="/finance" 
 *   element={
 *     <ProtectedRoute resource="finance" action="read">
 *       <Finance />
 *     </ProtectedRoute>
 *   } 
 * />
 */
export const ProtectedRoute = ({ 
  resource, 
  action = 'read',
  children,
  redirectTo = '/',
  showError = true
}) => {
  const { hasPermission, userRole } = usePermissions();
  
  if (!hasPermission(resource, action)) {
    if (showError) {
      return (
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="max-w-md w-full">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="mt-2">
                <p className="font-semibold">Accès refusé</p>
                <p className="text-sm mt-1">
                  Vous n'avez pas la permission d'accéder à cette page.
                </p>
                <p className="text-xs mt-2 text-gray-500">
                  Permission requise : {resource}.{action} (Votre rôle : {userRole})
                </p>
              </AlertDescription>
            </Alert>
            
            <div className="mt-4 text-center">
              <a href="/" className="text-emerald-600 hover:text-emerald-700 text-sm">
                ← Retour au tableau de bord
              </a>
            </div>
          </div>
        </div>
      );
    }
    
    return <Navigate to={redirectTo} replace />;
  }
  
  return <>{children}</>;
};
